<template>
  <NModal
    :bordered="false"
    class="cb-dynamic-data-table-settings"
    :onEsc="() => setComponentEditMode(false)"
    preset="card"
    :show="true"
    size="huge"
    :style="{
      '--n-border-radius': '16px',
      '--n-padding-top': '16px',
      '--n-padding-right': '16px',
      '--n-padding-bottom': '16px',
      '--n-padding-left': '16px',
    }"
    :title="t('canvasUI.canvasBuilder.dynamicDataTable.editDynamicDataTableComponent')"
    @close="setComponentEditMode(false)"
  >
    <NCard
      aria-modal="true"
      :bordered="false"
      class="flex"
      embedded
      role="dialog"
      :style="{
        '--n-color-embedded-modal': themeVars.base,
        '--n-padding-top': 0,
        '--n-padding-right': 0,
        '--n-padding-bottom': 0,
        '--n-padding-left': 0,
      }"
    >
      <div class="cb-dynamic-data-table-settings__card overflow-auto">
        <div class="flex flex-col h-full">
          <div class="flex flex-col h-full">
            <div class="flex-grow overflow-y-auto overflow-x-hidden">
              <NSteps v-model:current="currentStep" class="mx-1 mt-1 mb-8" size="small">
                <NStep :title="t('canvasUI.canvasBuilder.dynamicDataTable.data')" />
                <NStep
                  :disabled="!selectedArrayProperties || !selectedPropertyValues.length"
                  :title="t('canvasUI.canvasBuilder.dynamicDataTable.filters')"
                />
                <NStep
                  :disabled="!selectedArrayProperties || !selectedPropertyValues.length"
                  :title="t('canvasUI.canvasBuilder.dynamicDataTable.sortAndFormat')"
                />
                <NStep
                  :disabled="!selectedArrayProperties || !selectedPropertyValues.length"
                  :title="t('canvasUI.canvasBuilder.dynamicDataTable.customization')"
                />
              </NSteps>

              <template v-if="currentStep === 1">
                <NAlert
                  v-if="printablePropertiesCount > 10 && !alertClosed"
                  class="mb-4"
                  closable
                  type="warning"
                  @close="alertClosed = true"
                >
                  {{ t('canvasUI.canvasBuilder.dynamicDataTable.tooManyPrintableProperties') }}
                </NAlert>
                <div class="pa-4 border-solid border-primary5 rounded-2">
                  <NTreeSelect
                    v-if="!!crmShape"
                    block-line
                    filterable
                    :on-update:value="handleCollectionSelection"
                    :options="treeData.options"
                    :override-default-node-click-behavior="
                      ({ option }) => (option.children ? 'toggleExpand' : 'default')
                    "
                    :value="selectedArrayProperties?.path"
                  />

                  <div v-if="selectedArrayProperties" class="flex gap-4">
                    <div class="flex-1 mt-4">
                      <div class="font-500 tracking-[1.25px]">
                        {{ t('canvasUI.canvasBuilder.dynamicDataTable.availablePropertiesFor') }}
                        {{ selectedArrayProperties.path }}
                      </div>

                      <NCheckbox
                        :checked="isAllSelected"
                        class="mt-2"
                        :indeterminate="isIndeterminate"
                        @update:checked="handleSelectAll"
                      >
                        {{ t('canvasUI.canvasBuilder.dynamicDataTable.selectAll') }}
                      </NCheckbox>

                      <div class="w-1/2 mt-2">
                        <CSearch v-model:value="search" placeholder="Search property..." size="small" />
                      </div>

                      <div class="flex flex-col gap-1 mt-2">
                        <div class="flex items-center text-xs">
                          <div class="flex-1">{{ t('canvasUI.canvasBuilder.dynamicDataTable.properties') }}</div>
                          <div class="flex-1 grow-1 w-24">
                            {{ t('canvasUI.canvasBuilder.dynamicDataTable.showInPrint') }}
                          </div>
                        </div>

                        <NCheckboxGroup
                          class="overflow-y-auto"
                          :class="{
                            'max-h-[195px]':
                              selectedLayout === 'column' && !(printablePropertiesCount > 10 && !alertClosed),
                            'max-h-[315px]':
                              selectedLayout === 'row' && !(printablePropertiesCount > 10 && !alertClosed),
                            'max-h-[129px]':
                              selectedLayout === 'column' && printablePropertiesCount > 10 && !alertClosed,
                            'max-h-[249px]': selectedLayout === 'row' && printablePropertiesCount > 10 && !alertClosed,
                          }"
                          :value="selectedPropertyValues.map((prop) => prop.key)"
                          @update:value="handlePropertySelection"
                        >
                          <div class="flex flex-col">
                            <div v-for="prop in filteredArrayProperties" :key="prop" class="flex items-center py-1">
                              <div class="flex-1">
                                <NCheckbox :label="prop" :value="prop" />
                              </div>
                              <div class="w-24 flex-1 grow-1">
                                <div class="flex items-center gap-1">
                                  <NSwitch
                                    class="ml-2"
                                    :disabled="!selectedPropertyValues.some((p) => p.key === prop) || isPropGroup(prop)"
                                    size="small"
                                    :value="isPrintable(prop)"
                                    @update:value="(val) => updatePropertyPrintable(prop, val)"
                                  />
                                  <CHelpText v-if="isPropGroup(prop)" class="ml-1">
                                    {{ t('canvasUI.canvasBuilder.dynamicDataTable.groupPropAlwaysVisible') }}
                                  </CHelpText>
                                </div>
                              </div>
                            </div>
                          </div>
                        </NCheckboxGroup>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="selectedLayout === 'column' && selectedPropertyValues.length > 0"
                  class="mt-4 pa-4 border-solid border-primary5 rounded-2"
                >
                  <div class="font-500 tracking-[1.25px] mb-2">
                    {{ t('canvasUI.canvasBuilder.dynamicDataTable.grouping') }}
                  </div>
                  <div class="w-1/2">
                    <CSelect
                      bordered
                      clearable
                      :default-value="'none'"
                      :options="groupingOptions"
                      placeholder="Select grouping property"
                      :value="selectedGroupKey"
                      @update:value="(val: string | null) => (selectedGroupKey = val ?? 'none')"
                    />
                  </div>
                </div>
              </template>
              <template v-if="currentStep === 2">
                <CQueryBuilder v-model="filters" :attributes="allPropertyKeys" class="max-h-[530px] overflow-y-auto" />
              </template>
              <template v-if="currentStep === 3">
                <div v-if="selectedPropertyValues.length > 0" class="flex-1 mt-4 max-h-[530px] overflow-y-auto">
                  <VueDraggable v-model="selectedPropertyValues" class="flex flex-col gap-2" handle=".drag-handle">
                    <div v-for="prop in selectedPropertyValues" :key="prop.key" class="flex items-center gap-2">
                      <CIcon class="drag-handle mr-2 cursor-move" icon="grip-dots-vertical" size="20" />
                      <div class="flex-grow rounded-md px-4 py-2 bg-base bg-primary6">
                        <div class="flex items-center justify-between gap-2">
                          <div class="font-500 flex-shrink-0 color-primary text-base">{{ prop.key }}</div>
                          <div class="flex items-center gap-2 flex-1 justify-end">
                            <CInput
                              v-model:value="prop.display_name"
                              class="bg-primary6 border-primary6 flex-1 max-w-[400px]"
                              :input-props="{ class: 'border-primary6' }"
                              :placeholder="t('canvasUI.canvasBuilder.dynamicDataTable.displayName')"
                              size="small"
                              :style="{ '--n-border': `1px solid ${themeVars.primary5}` }"
                            />
                            <NCheckbox
                              v-if="selectedLayout === 'column'"
                              v-model:checked="prop.sort.enabled"
                              size="small"
                            >
                              {{ t('canvasUI.canvasBuilder.dynamicDataTable.sortable') }}
                            </NCheckbox>
                          </div>
                        </div>
                        <div
                          v-if="prop.sort.enabled && selectedLayout === 'column'"
                          class="flex gap-2 justify-between items-center mt-2 pt-2 border-t border-t-solid border-primary5"
                        >
                          <div
                            class="flex gap-2 items-center font-medium"
                            :class="{
                              'opacity-30': !prop.sort.default,
                            }"
                          >
                            {{ t('canvasUI.canvasBuilder.dynamicDataTable.sorting') }}
                            <CButton
                              :disabled="!prop.sort.default"
                              size="tiny"
                              :type="prop.sort.direction === 'asc' ? 'primary' : 'secondary'"
                              @click="prop.sort.direction = 'asc'"
                            >
                              {{ t('canvasUI.canvasBuilder.dynamicDataTable.asc') }}
                            </CButton>
                            <CButton
                              :disabled="!prop.sort.default"
                              size="tiny"
                              :type="prop.sort.direction === 'desc' ? 'primary' : 'secondary'"
                              @click="prop.sort.direction = 'desc'"
                            >
                              {{ t('canvasUI.canvasBuilder.dynamicDataTable.desc') }}
                            </CButton>
                          </div>
                          <CButton
                            bordered
                            size="small"
                            :type="prop.sort.default ? 'primary' : 'secondary'"
                            @click="setDefaultSort(prop.key)"
                          >
                            <template #icon>
                              <CIcon v-if="prop.sort.default" icon="check" size="12" />
                            </template>
                            {{ t('canvasUI.canvasBuilder.dynamicDataTable.default') }}
                          </CButton>
                        </div>
                        <div
                          class="flex gap-2 justify-between items-center mt-2 pt-2 border-t border-t-solid border-primary5"
                        >
                          <div class="flex gap-2 items-center font-medium">
                            {{ t('canvasUI.canvasBuilder.dynamicDataTable.format') }}
                          </div>
                          <div class="flex gap-2 items-center">
                            <CSelect
                              class="w-[120px]"
                              clearable
                              :options="formatTypeOptions"
                              placeholder="None"
                              size="small"
                              :value="prop.format?.type"
                              @update:value="(value: string | null) => updatePropertyFormat(prop.key, 'type', value)"
                            />
                            <template v-if="prop.format?.type === 'currency'">
                              <CSelect
                                class="w-[80px]"
                                :options="currencyOptions"
                                placeholder="USD"
                                size="small"
                                :value="prop.format?.currency ?? 'USD'"
                                @update:value="
                                  (value: string | null) => updatePropertyFormat(prop.key, 'currency', value)
                                "
                              />
                            </template>
                            <template
                              v-if="
                                prop.format?.type === 'number' ||
                                prop.format?.type === 'currency' ||
                                prop.format?.type === 'percentage'
                              "
                            >
                              <NInputNumber
                                class="w-[80px]"
                                :max="10"
                                :min="0"
                                placeholder="2"
                                size="small"
                                :value="prop.format?.decimals ?? 2"
                                @update:value="(val) => updatePropertyFormat(prop.key, 'decimals', val)"
                              />
                              <NCheckbox
                                :checked="prop.format?.thousandsSeparator ?? true"
                                size="small"
                                @update:checked="(val) => updatePropertyFormat(prop.key, 'thousandsSeparator', val)"
                              >
                                1,000
                              </NCheckbox>
                            </template>
                            <template v-if="prop.format?.type === 'date'">
                              <CSelect
                                class="w-[140px]"
                                :options="dateFormatOptions"
                                placeholder="MM/DD/YYYY"
                                size="small"
                                :value="prop.format?.dateFormat ?? 'MM/DD/YYYY'"
                                @update:value="
                                  (val: string | null) => updatePropertyFormat(prop.key, 'dateFormat', val)
                                "
                              />
                            </template>
                          </div>
                        </div>
                      </div>
                    </div>
                  </VueDraggable>
                </div>
              </template>
              <template v-if="currentStep === 4">
                <div class="flex-1 max-h-[530px] overflow-y-auto">
                  <div>
                    <div class="font-500 tracking-[1.25px] mb-2">
                      {{ t('canvasUI.canvasBuilder.dynamicDataTable.layoutType') }}
                    </div>

                    <NRadioGroup v-model:value="selectedLayout" class="mt-2">
                      <div class="flex gap-6">
                        <NRadio value="column">
                          {{ t('canvasUI.canvasBuilder.dynamicDataTable.columnLayout') }}
                        </NRadio>
                        <NRadio value="row">
                          {{ t('canvasUI.canvasBuilder.dynamicDataTable.rowLayout') }}
                        </NRadio>
                      </div>
                    </NRadioGroup>
                  </div>

                  <div v-if="selectedLayout === 'column'" class="mt-4">
                    <div class="font-500 tracking-[1.25px] mb-2">
                      {{ t('canvasUI.canvasBuilder.dynamicDataTable.rowStriping') }}
                    </div>

                    <NCheckbox v-model:checked="enableRowStriping">
                      {{ t('canvasUI.canvasBuilder.dynamicDataTable.enableRowStriping') }}
                    </NCheckbox>

                    <div v-if="enableRowStriping" class="mt-4 pl-6">
                      <div class="font-500 tracking-[1.25px] mb-2">
                        {{ t('canvasUI.canvasBuilder.dynamicDataTable.stripingColor') }}
                      </div>

                      <CSwatchSelector
                        v-if="enableCanvasTokens"
                        v-model="stripingColor"
                        class="!w-1/2"
                        :colors="[themeVars.primary6_1, themeVars.primary6, ...palette]"
                      />
                      <NColorPicker
                        v-else
                        v-model:value="stripingColor"
                        :actions="['clear', 'confirm']"
                        class="!w-1/2"
                        :default-value="null"
                        :modes="['hex']"
                        :swatches="[themeVars.primary6_1, themeVars.primary6, ...palette]"
                        :updateInputModelOnDrop="true"
                      />

                      <div class="mt-4">
                        <div class="font-500 tracking-[1.25px] mb-2">
                          {{ t('canvasUI.canvasBuilder.dynamicDataTable.stripingStartPosition') }}
                        </div>

                        <NRadioGroup v-model:value="stripingStartFromFirst" class="mt-2">
                          <div class="flex gap-6">
                            <NRadio :value="false">
                              {{ t('canvasUI.canvasBuilder.dynamicDataTable.startFromSecondRow') }}
                            </NRadio>
                            <NRadio :value="true">
                              {{ t('canvasUI.canvasBuilder.dynamicDataTable.startFromFirstRow') }}
                            </NRadio>
                          </div>
                        </NRadioGroup>
                      </div>
                    </div>
                  </div>

                  <div v-if="selectedLayout === 'column' && selectedGroupKey === 'none'" class="mt-4">
                    <div class="font-500 tracking-[1.25px] mb-2">
                      {{ t('canvasUI.canvasBuilder.dynamicDataTable.pagination') }}
                    </div>

                    <NCheckbox v-model:checked="enablePagination">
                      {{ t('canvasUI.canvasBuilder.dynamicDataTable.enablePagination') }}
                    </NCheckbox>

                    <div v-if="enablePagination" class="mt-2 pl-6">
                      <div class="font-500 tracking-[1.25px] mb-2">
                        {{ t('canvasUI.canvasBuilder.dynamicDataTable.pageSize') }}
                      </div>
                      <NInputNumber v-model:value="paginationSize" class="w-[96px]" :min="0" placeholder="" />
                    </div>
                  </div>

                  <div class="mt-4">
                    <div class="font-500 tracking-[1.25px] mb-2">
                      {{ t('canvasUI.canvasBuilder.dynamicDataTable.fontFamily') }}
                    </div>

                    <CFontSelect v-model="dataTableTheme.font_family" bordered class="!w-1/2" render-font-preview />
                  </div>

                  <div class="mt-4">
                    <div class="font-500 tracking-[1.25px] mb-2">
                      {{ t('canvasUI.canvasBuilder.dynamicDataTable.headerColor') }}
                    </div>

                    <CSwatchSelector
                      v-if="enableCanvasTokens"
                      v-model="dataTableTheme.background_color as string"
                      class="!w-1/2"
                      :colors="palette"
                      include-transparent
                    />
                    <NColorPicker
                      v-else
                      v-model:value="dataTableTheme.background_color"
                      :actions="['clear', 'confirm']"
                      class="!w-1/2"
                      :default-value="null"
                      :modes="['hex']"
                      :swatches="palette"
                      :updateInputModelOnDrop="true"
                    />
                  </div>

                  <div v-if="selectedLayout === 'column' && selectedGroupKey !== 'none'" class="mt-4">
                    <div class="font-500 tracking-[1.25px] mb-2">
                      {{ t('canvasUI.canvasBuilder.dynamicDataTable.groupHeaderColor') }}
                    </div>

                    <CSwatchSelector
                      v-if="enableCanvasTokens"
                      v-model="dataTableTheme.group_header_color as string"
                      class="!w-1/2"
                      :colors="palette"
                      include-transparent
                    />
                    <NColorPicker
                      v-else
                      v-model:value="dataTableTheme.group_header_color"
                      :actions="['clear', 'confirm']"
                      class="!w-1/2"
                      :default-value="null"
                      :modes="['hex']"
                      :swatches="palette"
                      :updateInputModelOnDrop="true"
                    />
                  </div>

                  <div
                    v-if="selectedLayout === 'column' && selectedGroupKey !== 'none' && !isScribbleChild"
                    class="mt-4"
                  >
                    <div class="font-500 tracking-[1.25px] mb-2">
                      {{ t('canvasUI.canvasBuilder.dynamicDataTable.printOptions') }}
                    </div>

                    <NCheckbox v-model:checked="breakGroupsOnNewPage">
                      {{ t('canvasUI.canvasBuilder.dynamicDataTable.breakGroupsOnNewPage') }}
                    </NCheckbox>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </NCard>
    <template #footer>
      <div class="flex justify-between gap-4">
        <CButton @click="onCancel">
          {{ t('canvasUI.common.cancel') }}
        </CButton>

        <div class="flex gap-2">
          <CButton class="w-10 h-10" :disabled="currentStep === 1" @click="currentStep--">
            <template #icon>
              <CIcon icon="arrow-left" size="16px" />
            </template>
          </CButton>
          <CButton
            class="w-10 h-10"
            :disabled="currentStep === 4 || !selectedArrayProperties || !selectedPropertyValues.length"
            @click="currentStep++"
          >
            <template #icon>
              <CIcon icon="arrow-right" size="16px" />
            </template>
          </CButton>
          <CButton
            :disabled="!selectedArrayProperties || !selectedPropertyValues.length"
            type="primary"
            @click="onSave"
          >
            {{ t('canvasUI.common.save') }}
          </CButton>
        </div>
      </div>
    </template>
  </NModal>
</template>

<script setup lang="ts">
import {
  NCard,
  NCheckbox,
  NCheckboxGroup,
  NColorPicker,
  NInputNumber,
  NModal,
  NRadio,
  NRadioGroup,
  NStep,
  NSteps,
  NTreeSelect,
  NSwitch,
  NAlert,
} from 'naive-ui'
import { computed, inject, onMounted, ref, toRef, type ComputedRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { VueDraggable } from 'vue-draggable-plus'
import { isNil } from 'lodash-es'

import CButton from '@lib/components/CButton/CButton.vue'
import CFontSelect from '@lib/components/CFontSelect/CFontSelect.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CInput from '@lib/components/CInput/CInput.vue'
import CSearch from '@lib/components/CSearch/CSearch.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CSwatchSelector from '@lib/components/CSwatchSelector/CSwatchSelector.vue'
import CQueryBuilder from '@lib/components/CQueryBuilder/CQueryBuilder.vue'
import CHelpText from '@lib/components/CHelpText/CHelpText.vue'

import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasTheme from '@lib/apps/canvas-builder/composables/useCanvasTheme'
import useCrmShape from '@canvas-builder/composables/useCrmShape'
import useThemeVars from '@lib/composables/useThemeVars'

import { transformToArrayTree } from '@lib/util/tree-transformation.util'
import type {
  ComponentNode,
  ComponentNodeDynamicDataTableThemeMeta,
  DynamicDataTableProps,
  DynamicDatatableProperty,
} from '@canvas-builder/types/canvas'
import { ComponentTypes } from '@canvas-builder/types/canvas'

import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { DEFAULT_FONT } from '@canvas-builder/constants'
import type { QueryAttribute, QueryOperator } from '@lib/types/query-builder.types'

interface ArrayProperties {
  path: string
  properties: string[]
}

const props = defineProps<{
  id: ComponentNode['id']
  data: DynamicDataTableProps | null
  theme_meta: ComponentNode['theme_meta']
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { t } = useI18n()
const { areTokenConstraintsActive, setComponentEditMode, updateNodeDataById, patchNodeById, getParentType } =
  useCanvas()
const { palette } = useCanvasTheme()
const { crmShape } = useCrmShape()
const themeVars = useThemeVars()

const themeMeta = computed(() => props.theme_meta as ComponentNodeDynamicDataTableThemeMeta)

// Check if this component is inside a scribble component
const parentType = getParentType(toRef(() => props.id))
const isScribbleChild = computed(() => parentType.value === ComponentTypes.Scribble)

const snapshot = ref(JSON.parse(JSON.stringify(props.data)))
const currentStep = ref(1)
const dataTableTheme = ref({
  background_color:
    launchDarkly.value.enable_themes &&
    !isNil(themeMeta.value?.dt_header_bg_palette_index) &&
    themeMeta.value.dt_header_bg_palette_index >= 0
      ? (palette.value[themeMeta.value.dt_header_bg_palette_index] ?? '')
      : (props.data?.theme?.background_color ?? null),
  group_header_color:
    launchDarkly.value.enable_themes &&
    !isNil(themeMeta.value?.dt_group_header_bg_palette_index) &&
    themeMeta.value.dt_group_header_bg_palette_index >= 0
      ? (palette.value[themeMeta.value.dt_group_header_bg_palette_index] ?? '')
      : (props.data?.theme?.group_header_color ?? null),
  font_family: props.data?.theme?.font_family ?? DEFAULT_FONT,
})

const enablePagination = ref(!!props.data?.page_size)
const breakGroupsOnNewPage = ref(props.data?.break_groups_on_new_page !== false)
// Default to true unless explicitly set to false for backwards compatibility
const enableRowStriping = ref(props.data?.enable_row_striping !== false)
const stripingColor = ref<string | undefined>(
  props.data?.enable_row_striping !== false
    ? (props.data?.striping_color ?? themeVars.value.primary6_1)
    : (props.data?.striping_color ?? undefined),
)
const stripingStartFromFirst = ref(
  props.data?.enable_row_striping !== false
    ? (props.data?.striping_start_from_first ?? false)
    : (props.data?.striping_start_from_first ?? false),
)
const filters = ref<QueryAttribute[]>(
  props.data?.filters
    ? props.data.filters.map((f) => ({
        property: f.property,
        operator: f.operator as QueryOperator,
        value: f.value,
      }))
    : [],
)

const paginationSize = ref(props.data?.page_size ?? 10)
const printableState = ref<Record<string, boolean>>({})
const search = ref('')
const alertClosed = ref(false)
const selectedArrayProperties = ref<ArrayProperties | null>(
  props.data?.path && props.data?.properties ? { path: props.data?.path ?? '', properties: [] } : null,
)
const selectedPropertyValues = ref<DynamicDatatableProperty[]>(
  props.data?.properties?.map((prop) => ({
    key: typeof prop === 'string' ? prop : prop.key,
    display_name: typeof prop === 'string' ? '' : prop.display_name,
    sort: prop.sort,
    group: prop.group,
    printable: prop.printable !== false,
    format: prop.format ?? {},
  })) ?? [],
)
const selectedLayout = ref<'column' | 'row'>(props.data?.layout ?? 'column')
const enableCanvasTokens = computed(() => !!launchDarkly.value.enable_canvas_tokens && areTokenConstraintsActive.value)
const filteredArrayProperties = computed(() => {
  return selectedArrayProperties.value?.properties.filter((prop) =>
    prop.toLowerCase().includes(search.value.toLowerCase()),
  )
})

const printablePropertiesCount = computed(() => {
  return selectedPropertyValues.value.filter((prop) => prop.printable !== false).length
})

const allPropertyKeys = computed(() =>
  (selectedArrayProperties.value?.properties ?? []).map((key) => ({
    label: key,
    value: key,
  })),
)

const isAllSelected = computed(() => {
  if (!selectedArrayProperties.value) return false
  return selectedArrayProperties.value.properties.length === selectedPropertyValues.value.length
})

const isIndeterminate = computed(() => {
  if (!selectedArrayProperties.value) return false
  return (
    selectedPropertyValues.value.length > 0 &&
    selectedPropertyValues.value.length < selectedArrayProperties.value.properties.length
  )
})

const treeData = computed(() => ({
  options: transformToArrayTree(crmShape.value ?? {}),
}))

const selectedGroupKey = ref<string>('none')

const groupingOptions = computed(() => [
  { label: t('canvasUI.common.none'), value: 'none', default: true },
  ...selectedPropertyValues.value.map((prop) => ({
    label: prop.key,
    value: prop.key,
  })),
])

const formatTypeOptions = computed(() => [
  { label: t('canvasUI.common.none'), value: undefined },
  { label: t('canvasUI.canvasBuilder.dynamicDataTable.currency'), value: 'currency' },
  { label: t('canvasUI.canvasBuilder.dynamicDataTable.number'), value: 'number' },
  { label: t('canvasUI.canvasBuilder.dynamicDataTable.percentage'), value: 'percentage' },
  { label: t('canvasUI.canvasBuilder.dynamicDataTable.date'), value: 'date' },
])

const currencyOptions = computed(() => [
  { label: 'USD', value: 'USD' },
  { label: 'EUR', value: 'EUR' },
  { label: 'GBP', value: 'GBP' },
  { label: 'JPY', value: 'JPY' },
  { label: 'CHF', value: 'CHF' },
  { label: 'CAD', value: 'CAD' },
  { label: 'AUD', value: 'AUD' },
])

const dateFormatOptions = computed(() => [
  { label: 'MM/DD/YYYY', value: 'MM/DD/YYYY' },
  { label: 'DD/MM/YYYY', value: 'DD/MM/YYYY' },
  { label: 'YYYY-MM-DD', value: 'YYYY-MM-DD' },
  { label: 'MMM DD, YYYY', value: 'MMM DD, YYYY' },
  { label: 'DD MMM YYYY', value: 'DD MMM YYYY' },
])

watch(selectedGroupKey, (newValue) => {
  // Reset to 'none' if cleared or invalid
  if (!newValue || typeof newValue !== 'string') {
    selectedGroupKey.value = 'none'
    return
  }

  // Disable pagination when grouping is enabled
  if (newValue !== 'none') {
    enablePagination.value = false
    // Only set break_groups_on_new_page to true by default if it's currently undefined
    // However, if inside a scribble component, always set to false
    if (isScribbleChild.value) {
      breakGroupsOnNewPage.value = false
    } else if (props.data?.break_groups_on_new_page === undefined) {
      breakGroupsOnNewPage.value = true
    }
  }

  // Update all properties' group status and ensure group property is printable
  selectedPropertyValues.value = selectedPropertyValues.value.map((prop) => ({
    ...prop,
    group: prop.key === newValue && newValue !== 'none',
    printable: prop.key === newValue && newValue !== 'none' ? true : prop.printable,
  }))
})

watch(selectedLayout, (newValue) => {
  if (newValue === 'row') {
    selectedGroupKey.value = 'none'
    enableRowStriping.value = false
    stripingColor.value = undefined
    stripingStartFromFirst.value = false
    selectedPropertyValues.value = selectedPropertyValues.value.map((prop) => ({
      ...prop,
      group: false,
    }))
  } else if (newValue === 'column') {
    // Set defaults when switching to column layout
    if (enableRowStriping.value === false) {
      enableRowStriping.value = true
      stripingColor.value = themeVars.value.primary6_1
      stripingStartFromFirst.value = false
    }
  }
})

watch(enableRowStriping, (newValue, oldValue) => {
  // When user enables row striping, automatically set color to primary6_1
  if (newValue === true && oldValue === false) {
    stripingColor.value = themeVars.value.primary6_1
    stripingStartFromFirst.value = false
  }
})

// Watch for changes in parent type to handle scribble component behavior
watch(
  isScribbleChild,
  (isInside) => {
    if (isInside && selectedGroupKey.value !== 'none') {
      breakGroupsOnNewPage.value = false
    }
  },
  { immediate: true },
)

function handleCollectionSelection(value: string) {
  selectedPropertyValues.value = []
  const arrayData = crmShape.value ?? {}
  const pathParts = value.split('.')
  let currentData: any = arrayData

  for (const part of pathParts) {
    currentData = currentData[part]
  }

  if (Array.isArray(currentData)) {
    const firstItem = currentData[0]

    if (!firstItem || typeof firstItem !== 'object') {
      selectedArrayProperties.value = null
      return
    }

    selectedArrayProperties.value = {
      path: value,
      properties: Object.keys(firstItem),
    }

    // Initialize properties with default values
    selectedPropertyValues.value = selectedArrayProperties.value.properties.map((prop) => ({
      key: prop,
      display_name: '',
      sort: {
        direction: 'asc',
        enabled: false,
      },
      group: false,
      printable: true,
      format: {},
    }))
  } else {
    selectedArrayProperties.value = null
  }
}

function isPrintable(propKey: string): boolean {
  if (isPropGroup(propKey)) {
    return true
  }

  const selectedProp = selectedPropertyValues.value.find((p) => p.key === propKey)
  if (selectedProp) {
    return selectedProp.printable !== false
  }

  return printableState.value[propKey] !== false
}

function isPropGroup(propKey: string): boolean {
  return selectedGroupKey.value === propKey && selectedGroupKey.value !== 'none'
}

function handleSelectAll(checked: boolean) {
  if (checked) {
    selectedPropertyValues.value = (selectedArrayProperties.value?.properties ?? []).map((prop) => ({
      key: prop,
      display_name: '',
      sort: {
        direction: 'asc',
        enabled: false,
      },
      group: false,
      printable: isPrintable(prop),
      format: {},
    }))
  } else {
    selectedPropertyValues.value.forEach((prop) => {
      printableState.value[prop.key] = prop.printable !== false
    })
    selectedPropertyValues.value = []
  }
}

function handlePropertySelection(checkedKeys: (string | number)[]) {
  // Check if the currently grouped property is being removed
  if (selectedGroupKey.value !== 'none' && !checkedKeys.includes(selectedGroupKey.value)) {
    selectedGroupKey.value = 'none'
    // Reset the break groups on new page option when the grouped property is removed
    breakGroupsOnNewPage.value = false
  }

  const keysToRemove = selectedPropertyValues.value
    .filter((prop) => !checkedKeys.includes(prop.key))
    .map((prop) => prop.key)

  keysToRemove.forEach((key) => {
    const prop = selectedPropertyValues.value.find((p) => p.key === key)
    if (prop) {
      printableState.value[key] = prop.printable !== false
    }
  })

  // Find existing properties to preserve their settings
  const existingProps = new Map(selectedPropertyValues.value.map((prop) => [prop.key, prop]))

  selectedPropertyValues.value = checkedKeys.map((key) => {
    const existingProp = existingProps.get(String(key))
    const keyStr = String(key)

    return {
      key: keyStr,
      display_name: existingProp?.display_name ?? '',
      sort: existingProp?.sort ?? {
        direction: 'asc',
        enabled: false,
      },
      group: key === selectedGroupKey.value && selectedGroupKey.value !== 'none',
      printable: existingProp?.printable ?? printableState.value[keyStr] !== false,
      format: existingProp?.format ?? {},
    }
  })
}

function updatePropertyPrintable(propKey: string, value: boolean) {
  if (isPropGroup(propKey)) {
    return
  }

  printableState.value[propKey] = value

  selectedPropertyValues.value = selectedPropertyValues.value.map((prop) => {
    if (prop.key === propKey) {
      return {
        ...prop,
        printable: value,
      }
    }
    return prop
  })
}

function updatePropertyFormat(propKey: string, formatKey: string, value: any) {
  selectedPropertyValues.value = selectedPropertyValues.value.map((prop) => {
    if (prop.key === propKey) {
      if (!prop.format) {
        prop.format = {}
      }
      return {
        ...prop,
        format: {
          ...prop.format,
          [formatKey]: value,
        },
      }
    }
    return prop
  })
}

function onCancel() {
  if (snapshot.value) {
    updateNodeDataById(props.id, snapshot.value)
  }
  setComponentEditMode(false)
}

function onSave() {
  if (launchDarkly.value.enable_themes) {
    const headerIndex = palette.value.findIndex((p) => p === dataTableTheme.value.background_color)
    const groupHeaderIndex = palette.value.findIndex((p) => p === dataTableTheme.value.group_header_color)
    updateComponentNode({
      theme_meta: {
        ...themeMeta.value,
        dt_header_bg_palette_index: headerIndex !== -1 ? headerIndex : undefined,
        dt_group_header_bg_palette_index: groupHeaderIndex !== -1 ? groupHeaderIndex : undefined,
      },
    })
  }

  // Determine if grouping is enabled (column layout with a selected group)
  const hasGrouping = selectedLayout.value === 'column' && selectedGroupKey.value !== 'none'

  // Prepare the node data update
  const nodeData: DynamicDataTableProps = {
    filters: filters.value,
    layout: selectedLayout.value,
    page_size: enablePagination.value ? paginationSize.value : undefined,
    path: selectedArrayProperties.value?.path ?? '',
    properties: selectedPropertyValues.value,
    theme: dataTableTheme.value,
    enable_row_striping: selectedLayout.value === 'column' ? enableRowStriping.value : undefined,
    striping_color:
      selectedLayout.value === 'column' && enableRowStriping.value
        ? (stripingColor.value ?? themeVars.value.primary6_1)
        : undefined,
    striping_start_from_first:
      selectedLayout.value === 'column' && enableRowStriping.value
        ? (stripingStartFromFirst.value ?? false)
        : undefined,
  }

  if (hasGrouping) {
    nodeData.break_groups_on_new_page = breakGroupsOnNewPage.value
  }

  updateNodeDataById(props.id, nodeData)
  setComponentEditMode(false)
}

function setSelectedKeys(properties: DynamicDatatableProperty[]) {
  selectedPropertyValues.value = properties.map((prop) => ({
    key: typeof prop === 'string' ? prop : prop.key,
    display_name: typeof prop === 'string' ? '' : prop.display_name,
    sort: prop.sort ?? { enabled: false },
    group: prop.group ?? false,
    printable: prop.printable !== false,
    format: prop.format ?? {},
  }))

  // Set initial group selection
  const groupedProp = properties.find((prop) => prop.group)
  selectedGroupKey.value = groupedProp?.key ?? 'none'
}

function setDefaultSort(key: string) {
  selectedPropertyValues.value = selectedPropertyValues.value.map((prop) => ({
    ...prop,
    sort: {
      ...prop.sort,
      direction: 'asc',
      default: prop.key === key && !prop.sort.default,
    },
  }))
}

function updateComponentNode(updatedNode: Record<string, any>) {
  const nodePatch = updatedNode as ComponentNode
  try {
    patchNodeById(props.id, nodePatch)
  } catch (error) {
    console.error(error)
  }
}

onMounted(() => {
  if (props.data?.path) {
    handleCollectionSelection(props.data.path)
    setSelectedKeys(props.data.properties)

    // Only set breakGroupsOnNewPage if there's grouping
    if (selectedGroupKey.value === 'none') {
      breakGroupsOnNewPage.value = false
    }
  }
})
</script>
<style lang="scss">
// TODO: fix the restoration of the theme group header color
.cb-dynamic-data-table-settings {
  width: 780px;
  height: clamp(350px, calc(100vh - 120px), 720px);
}

.cb-dynamic-data-table-settings__card {
  max-height: clamp(100px, calc(100vh - 260px), 588px);
}
</style>
